// src/pages/Profile.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { authHeader, handle401Error } from "../utils/auth";
import { API_BASE } from "../config";

/* ============== MAIN ============== */
export default function Profile() {
  const blue = "#3366CC";
  const text = "#0F172A";
  const navigate = useNavigate();
  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  // ====== STATE ======
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Profile data (ข้อมูลที่แสดงจริง)
  const [profileData, setProfileData] = useState({
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    industry: "",
    country: "",
    estimateUser: 0,
  });

  // Edit data (ข้อมูลที่กำลังแก้ไข ยังไม่บันทึก)
  const [editData, setEditData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    industry: "",
    country: "",
    estimateUser: 0,
  });
  
  // ====== CONFIGURATION ======
  // กำหนด editable fields (ใช้ร่วมกันในหลาย functions)
  const editableFields = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'company',
    'industry',
    'country',
    'estimateUser'
  ];

  // ====== HELPERS ======
  // ใช้ authHeader จาก utils/auth.js

  // ====== EDIT HANDLERS ======
  const handleEdit = () => {
    // Copy profileData ไป editData เมื่อเริ่มแก้ไข (dynamic approach)
    const initialEditData = {};
    editableFields.forEach(field => {
      if (field === 'estimateUser') {
        initialEditData[field] = profileData[field] || 0;
      } else {
        initialEditData[field] = profileData[field] || "";
      }
    });
    setEditData(initialEditData);
    setIsEditing(true);
    setError("");
  };

  const handleCancel = () => {
    // Reset editData และปิดโหมดแก้ไข (dynamic approach)
    const resetEditData = {};
    editableFields.forEach(field => {
      resetEditData[field] = field === 'estimateUser' ? 0 : "";
    });
    setEditData(resetEditData);
    setIsEditing(false);
    setError("");
  };

  const handleInputChange = (field, value) => {
    // Update editData โดยไม่กระทบ profileData
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
        setError("");

        const token = sessionStorage.getItem("access_token");
        if (!token) {
          navigate("/login");
          return;
        }

      // Validate userId
      const userId = profileData.id;
      if (!userId) {
        throw new Error("User ID not found.");
      }

      const userIdStr = String(userId);
      if (!/^[0-9a-fA-F-]+$/.test(userIdStr) || userIdStr.length > 50) {
        throw new Error("Invalid User ID.");
      }

      // เปรียบเทียบ editData กับ profileData เพื่อหาข้อมูลที่เปลี่ยนแปลง (dynamic approach)
      const profileChanges = {};
      
      // วนลูปเช็คความแตกต่างของแต่ละ field
      editableFields.forEach(field => {
        if (editData[field] !== profileData[field]) {
          profileChanges[field] = editData[field];
        }
      });

      // ถ้าไม่มีข้อมูลที่เปลี่ยนแปลง ไม่ต้องส่ง request
      if (Object.keys(profileChanges).length === 0) {
        setIsEditing(false);
        setSaving(false);
        return;
      }

      // Prepare data for API ตามรูปแบบที่ API ต้องการ
      const updatePayload = {
        requestType: "profile",
        profile: profileChanges,
      };

      // Send PATCH request to update client data
      const updateRes = await fetch(`${API_BASE}/clients/${encodeURIComponent(userIdStr)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify(updatePayload),
      });

      if (handle401Error(updateRes, navigate)) {
        return;
      }

      if (!updateRes.ok) {
        const err = await updateRes.json().catch(() => ({}));
        throw new Error(err?.detail || "Failed to save.");
      }

      // Update profileData ด้วยข้อมูลที่เปลี่ยนแปลงจริงๆ (ข้อมูลจะเปลี่ยนทันทีหลังจากบันทึกสำเร็จ)
      setProfileData((prev) => ({
        ...prev,
        ...profileChanges,
      }));

      // ปิดโหมดแก้ไข
      setIsEditing(false);
      } catch (e) {
      const errorMsg = e?.message || "An error occurred while saving.";
      setError(errorMsg.length > 200 ? "An error occurred while saving." : errorMsg);
      } finally {
      setSaving(false);
    }
  };

  // ====== FETCH PROFILE DATA ======
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError("");

        // Step 1: Fetch basic user info from /me to get user ID
        const meRes = await fetch(`${API_BASE}/me`, {
          headers: { "Content-Type": "application/json", ...authHeader() },
        });
        
        if (handle401Error(meRes, navigate)) {
          return;
        }
        
        if (!meRes.ok) {
          const err = await meRes.json().catch(() => ({}));
          throw new Error(err?.detail || "Failed to load profile.");
        }
        
        const meData = await meRes.json();
        const userId = meData?.id;
        
        if (!userId) {
          throw new Error("User ID not found from API.");
        }
        
        // Security: Validate userId to prevent path traversal/injection
        // Only allow numeric IDs or UUIDs
        const userIdStr = String(userId);
        if (!/^[0-9a-fA-F-]+$/.test(userIdStr) || userIdStr.length > 50) {
          throw new Error("Invalid User ID.");
        }
        
        // Step 2: Fetch detailed client information
        // Priority: ใช้ข้อมูลจาก /clients/{id} เป็นหลัก เพราะมีข้อมูลครบถ้วนกว่า
        // Security: Encode userId to prevent injection
        const clientRes = await fetch(`${API_BASE}/clients/${encodeURIComponent(userIdStr)}`, {
            headers: { "Content-Type": "application/json", ...authHeader() },
        });
        
        if (handle401Error(clientRes, navigate)) {
          return;
        }
        
        // ถ้า /clients/{id} ไม่มีข้อมูล ให้ใช้ข้อมูลจาก /me แทน
        let clientData = null;
        if (clientRes.ok) {
          clientData = await clientRes.json();
        }
        
        // Helper function to get value with fallback
        const getValue = (primary, fallback, defaultValue = "") => {
          return primary || fallback || defaultValue;
        };
        
        // Merge data: ใช้ clientData เป็นหลัก, fallback ไปที่ meData
        const mergedData = {
          id: userId,
          firstName: getValue(
            clientData?.firstName || clientData?.first_name,
            meData?.firstName || meData?.first_name
          ),
          lastName: getValue(
            clientData?.lastName || clientData?.last_name,
            meData?.lastName || meData?.last_name
          ),
          email: getValue(clientData?.email, meData?.email),
          phone: getValue(clientData?.phone, meData?.phone),
          company: getValue(clientData?.company, meData?.company),
          industry: getValue(clientData?.industry, meData?.industry),
          country: getValue(clientData?.country, meData?.country),
          estimateUser: clientData?.estimateUser || clientData?.estimate_user || meData?.estimateUser || meData?.estimate_user || 0,
        };
        
        setProfileData(mergedData);
      } catch (e) {
        // Security: Don't expose detailed error messages
        const errorMsg = e?.message || "An error occurred while loading data.";
        setError(errorMsg.length > 200 ? "An error occurred while loading data." : errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // ====== STYLE ======
  const container = {
    width: "100%",
    background: "#fff",
    overflow: "visible",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  };

  const responsiveCSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }
  .page-container { width: 100%; overflow-x: hidden; background: #fff; }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }
    .desktop-nav { display: none !important; }
    .hamburger-btn { display: inline-flex !important; }
    .sticky-nav { height: 60px !important; padding: 0 16px !important; }

    .profile-form-grid {
      grid-template-columns: 1fr !important;
    }
  }

  [id] { scroll-margin-top: 70px; }
`;



  return (
    <>
      <style>{responsiveCSS}</style>
      <div style={container}>
        {/* Sticky header */}
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        {/* Hero */}
        <section
          style={{ background: "#0E2B55", color: "#fff", padding: "28px 24px 30px" }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <h1
              style={{
                margin: 0,
                fontSize: 36,
                fontWeight: 800,
                letterSpacing: 0.2,
              }}
            >
              Profile 
            </h1>
            {!!profileData.company && (
              <p style={{ margin: "8px 0 0", color: "#93c5fd" }}>
                Organization: <b>{profileData.company}</b>
              </p>
            )}
            <p
              style={{
                margin: "10px 0 0",
                color: "#2BB0F6",
                opacity: 0.95,
                lineHeight: 1.55,
              }}
            >
              Manage your account information and preferences.
            </p>
          </div>
        </section>

        {/* Profile Form Section */}
        <section style={{ background: "#F3F6FB", padding: "42px 16px 80px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            {/* Profile Information Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
                border: "1px solid #E5E7EB",
                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                padding: "32px",
                marginBottom: 24,
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 24,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 999,
                      background: "linear-gradient(135deg, #3B82F6 0%, #2BB0F6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 20,
                    }}
                  >
                    {profileData.firstName?.[0]?.toUpperCase() || profileData.email?.[0]?.toUpperCase() || "U"}
              </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: "#0F172A" }}>
                      {profileData.firstName || profileData.lastName 
                        ? `${profileData.firstName} ${profileData.lastName}`.trim()
                        : "Profile Information"}
              </div>
                    <div style={{ fontSize: 14, color: "#64748B" }}>
                      {profileData.email || "No email"}
            </div>
              </div>
                </div>
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    aria-label="Edit profile information"
                    style={{
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "1px solid #3B82F6",
                      background: "#3B82F6",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#2563EB";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(59,130,246,0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#3B82F6";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    Edit Profile
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      aria-label={saving ? "Cancel editing (disabled, saving in progress)" : "Cancel editing profile"}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "1px solid #E2E8F0",
                        background: "#fff",
                        color: "#64748B",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: saving ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                        opacity: saving ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!saving) {
                          e.currentTarget.style.background = "#F8FAFC";
                          e.currentTarget.style.borderColor = "#CBD5E1";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!saving) {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      aria-label={saving ? "Saving profile changes (please wait)" : "Save profile changes"}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "1px solid #10B981",
                        background: "#10B981",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: saving ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                        opacity: saving ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!saving) {
                          e.currentTarget.style.background = "#059669";
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(16,185,129,0.4)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!saving) {
                          e.currentTarget.style.background = "#10B981";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div
                  role="alert"
                  aria-live="polite"
                  style={{
                    marginBottom: 20,
                    borderRadius: 10,
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    padding: "12px 16px",
                    color: "#b91c1c",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FiX size={16} /> {error}
              </div>
              )}

              {/* Loading State */}
            {loading && (
              <div
                  role="status"
                  aria-live="polite"
                  aria-label="Loading profile information"
                  style={{ padding: 40, textAlign: "center", color: "#64748B" }}
              >
                  Loading...
              </div>
            )}

              {/* Profile Information Display/Edit */}
              {!loading && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        First Name
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.firstName || ""}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          disabled={saving}
                          maxLength={50}
                      style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.firstName || "—"}
                    </div>
            )}
                      </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Last Name
                          </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.lastName || ""}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          disabled={saving}
                          maxLength={50}
                      style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.lastName || "—"}
                        </div>
                  )}
                </div>
                    </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Email
                      </div>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editData.email || ""}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          disabled={saving}
                          maxLength={254}
                                style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                <div
                  style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.email || "—"}
                            </div>
                      )}
                          </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Phone
                      </div>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editData.phone || ""}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          disabled={saving}
                          maxLength={20}
                                style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.phone || "—"}
                          </div>
                    )}
                  </div>
                </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Company
                        </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.company || ""}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          disabled={saving}
                          maxLength={100}
                          style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.company || "—"}
                          </div>
            )}
                        </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Industry
                          </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.industry || ""}
                          onChange={(e) => handleInputChange("industry", e.target.value)}
                          disabled={saving}
                          maxLength={50}
                          style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
      style={{
                            fontSize: 15,
        color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.industry || "—"}
                        </div>
                  )}
                          </div>
                </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Country
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.country || ""}
                          onChange={(e) => handleInputChange("country", e.target.value)}
                          disabled={saving}
                          maxLength={60}
                          style={{
  width: "100%", 
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
  border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                <div
                  style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.country || "—"}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Estimated Users
                      </div>
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          max={999999}
                          value={editData.estimateUser || 0}
                          onChange={(e) => handleInputChange("estimateUser", parseInt(e.target.value) || 0)}
                          disabled={saving}
                          style={{
                            width: "100%",
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid #CBD5E1",
                            background: saving ? "#F8FAFC" : "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            outline: "none",
                            cursor: saving ? "not-allowed" : "text",
                            opacity: saving ? 0.6 : 1,
                          }}
                          onFocus={(e) => {
                            if (!saving) {
                              e.target.style.borderColor = "#3B82F6";
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#CBD5E1";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 15,
                            color: "#0F172A",
                            fontWeight: 500,
                            padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid #E2E8F0",
                            background: "#F8FAFC",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          {profileData.estimateUser || 0}
                        </div>
                      )}
                  </div>
                  </div>
                </div>
            )}
                  </div>

          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
